import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, getToken, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatIDR, formatDate, BOOKING_STATUS } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Search, Download } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function BookingsPage() {
  const [bookings, setBookings] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [selected, setSelected] = React.useState([]);
  const nav = useNavigate();

  const load = React.useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    const { data } = await api.get("/bookings", { params });
    setBookings(data);
    setSelected([]);
  }, [q, status]);

  React.useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const toggleAll = () => setSelected(selected.length === bookings.length ? [] : bookings.map((r) => r.id));

  const bulkDelete = async () => {
    try { const { data } = await api.post("/bookings/bulk-delete", { ids: selected }); toast.success(`${data.deleted} booking dibatalkan`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="bookings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Booking</h1>
          <p className="text-sm text-neutral-500">Daftar semua booking equipment.</p>
        </div>
        <Button onClick={() => nav("/bookings/new")} className="bg-green-600 hover:bg-green-700" data-testid="btn-new-booking">
          <Plus className="h-4 w-4 mr-1" /> Booking Baru
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input placeholder="Cari nomor / project…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="bookings-search" />
        </div>
        <select
          className="border border-neutral-200 rounded-md px-3 py-2 text-sm bg-white"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="bookings-status-filter"
        >
          <option value="">Semua Status</option>
          {Object.entries(BOOKING_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <Button variant="outline" size="sm" onClick={async () => {
          const res = await api.get("/export/bookings", { responseType: "blob" });
          const a = document.createElement("a");
          a.href = URL.createObjectURL(res.data); a.download = "bookings.csv"; a.click();
        }} data-testid="export-bookings-csv"><Download className="h-4 w-4 mr-1" />Ekspor CSV</Button>
        {selected.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50" data-testid="bulk-delete-bookings">
                Hapus {selected.length} Terpilih
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Batalkan {selected.length} booking?</AlertDialogTitle>
                <AlertDialogDescription>Booking akan diubah statusnya menjadi Dibatalkan. Data historis tetap terjaga.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Batal</AlertDialogCancel>
                <AlertDialogAction onClick={bulkDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-bulk-delete-bookings">Ya, Batalkan</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="w-8"><input type="checkbox" checked={selected.length === bookings.length && bookings.length > 0} onChange={toggleAll} data-testid="select-all-bookings" /></th>
              <th className="text-left">No. Booking</th>
              <th className="text-left">Pelanggan</th>
              <th className="text-left">Project</th>
              <th className="text-left">Periode</th>
              <th className="text-right">Total</th>
              <th className="text-left">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.id} data-testid={`booking-row-${b.id}`}>
                <td><input type="checkbox" checked={selected.includes(b.id)} onChange={() => toggle(b.id)} data-testid={`select-bkg-${b.id}`} /></td>
                <td className="font-mono text-xs">{b.number}</td>
                <td>{b.customer_name}</td>
                <td>{b.project_name || "-"}</td>
                <td className="text-xs">{formatDate(b.start_date)} – {formatDate(b.end_date)}</td>
                <td className="text-right font-medium">{formatIDR(b.totals?.total)}</td>
                <td><StatusBadge statusMap={BOOKING_STATUS} value={b.status} /></td>
                <td className="text-right">
                  <Link to={`/bookings/${b.id}`} className="text-green-700 text-xs hover:underline" data-testid={`view-booking-${b.id}`}>Detail →</Link>
                </td>
              </tr>
            ))}
            {bookings.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-sm text-neutral-500">Belum ada booking.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
